import React, { createContext, useRef, RefObject, useEffect } from 'react';
import { useChain } from 'react-spring';

import { Head } from './seo';
import Layout from '../components/Layout';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import loadable, { LoadableComponent } from '@loadable/component';

import {
  HeroSectionType,
  PageSectionType,
  ProductsBySlugType,
  ReactSpringHook,
} from 'utils/types';
import {
  HeroContainer as HeroSection,
  HeroContainerProps,
} from '../containers/Homepage/HeroContainer';
import { HowItWorksContainerProps } from '../containers/Homepage/HowItWorksContainer';
const HowItWorksSection = loadable(
  () => import('../containers/Homepage/HowItWorksContainer'),
);
import { WhatToExpectContainerProps } from '../containers/Homepage/WhatToExpectContainer';
const WhatToExpectSection = loadable(
  () => import('../containers/Homepage/WhatToExpectContainer'),
);
import { LatestUpdatesContainerProps } from '../containers/Homepage/LatestUpdatesContainer';
const LatestUpdatesSection = loadable(
  () => import('../containers/Homepage/LatestUpdatesContainer'),
);
import { TestimonialContainerProps } from '../containers/Homepage/TestimonialContainer';
const TestimonialSection = loadable(
  () => import('../containers/Homepage/TestimonialContainer'),
);
import { ByTheNumbersContainerProps } from '../containers/Homepage/ByTheNumbersContainer';
const ByTheNumbersSection = loadable(
  () => import('../containers/Homepage/ByTheNumbersContainer'),
);
import { RecommendedArticlesContainerProps } from '../containers/Homepage/RecommendedArticlesContainer';
const RecommendedArticlesSection = loadable(
  () => import('../containers/Homepage/RecommendedArticlesContainer'),
);
import { BusinessPartnerContainerProps } from '../containers/Homepage/BusinessPartnerContainer';
const BusinessPartnerSection = loadable(
  () => import('../containers/Homepage/BusinessPartnerContainer'),
);
import { FAQContainerProps } from '../containers/Homepage/FAQContainer';
const FAQSection = loadable(
  () => import('../containers/Homepage/FAQContainer'),
);
import { PressContainerProps } from '../containers/Homepage/PressContainer';
const PressSection = loadable(
  () => import('../containers/Homepage/PressContainer'),
);
import {
  PopularTestsContainer as PopularTestsSection,
  PopularTestsContainerProps,
} from '../containers/Homepage/PopularTestsContainer';
import useDisplayedSectionFilterForMembers from '../utils/hooks/useDisplayedSectionFilterForMembers';

const components: {
  HeroSection: React.FunctionComponent<HeroContainerProps>;
  HowItWorksSection: LoadableComponent<HowItWorksContainerProps>;
  WhatToExpectSection: LoadableComponent<WhatToExpectContainerProps>;
  LatestUpdatesSection: LoadableComponent<LatestUpdatesContainerProps>;
  TestimonialSection: LoadableComponent<TestimonialContainerProps>;
  ByTheNumbersSection: LoadableComponent<ByTheNumbersContainerProps>;
  RecommendedArticlesSection: LoadableComponent<RecommendedArticlesContainerProps>;
  PopularTestsSection: React.FunctionComponent<PopularTestsContainerProps>;
  PressSection: LoadableComponent<PressContainerProps>;
  BusinessPartnerSection: LoadableComponent<BusinessPartnerContainerProps>;
  FAQSection: LoadableComponent<FAQContainerProps>;
} = {
  HeroSection,
  HowItWorksSection,
  WhatToExpectSection,
  LatestUpdatesSection,
  TestimonialSection,
  BusinessPartnerSection,
  ByTheNumbersSection,
  RecommendedArticlesSection,
  PressSection,
  PopularTestsSection,
  FAQSection,
};

interface ProductPromoData {
  slug: string;
  onSale: boolean;
  promoText: string;
}

interface PageContext {
  heroSection: HeroSectionType;
  sections: PageSectionType[];
  productsBySlug: ProductsBySlugType;
  componentName: string;
  title: string;
  allProductPromoData: ProductPromoData[];
}

interface Props {
  pageContext: PageContext;
}

export interface RefsContextType {
  [key: string]: RefObject<ReactSpringHook>;
}

export const ProductInfoContext = createContext<ProductsBySlugType>({});
export const RefsForAnimationContext = createContext<RefsContextType>({});
export const AllProductPromosContext = createContext<ProductPromoData[]>([]);

export const homepageTemplate: React.FC<Props> = ({ pageContext }) => {
  const {
    sections,
    heroSection,
    productsBySlug,
    allProductPromoData,
  } = pageContext;

  if (!sections || !heroSection) {
    return null;
  }

  // Refs will be passed down in RefsForAnimationContext
  const animation1 = useRef<ReactSpringHook>(null);
  const animation2 = useRef<ReactSpringHook>(null);
  const animation3 = useRef<ReactSpringHook>(null);
  const animation4 = useRef<ReactSpringHook>(null);

  // by default useChain will space each animation 500ms apart
  // the 2nd argument overrides this default and defines the timing of the animations:
  // 1st animation happens immediately - 2nd animation starts 450ms after first animation ends
  // 3rd and 4th animations start 750ms after the first animation ends
  useChain([animation1, animation2, animation3, animation4], [0, 0.2, 1, 1]);

  // displayedSections is an array of PageSectionType
  // setUser is passed down to Layout to grab the existing UserContext for filtering
  // `sections` are used to set initial state and if the user is not a member then the original array is returned
  // If the user is an active member, we filter out sections based on the slug
  const [displayedSections, setUser] = useDisplayedSectionFilterForMembers(sections);
  useEffect(() => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        Page: 'Homepage',
      },
    });
  }, []);
  return (
    <>
      <Head />
      <Layout productsBySlug={productsBySlug} setParentUserData={setUser}>
        <ProductInfoContext.Provider value={productsBySlug}>
          <AllProductPromosContext.Provider value={allProductPromoData}>
            <RefsForAnimationContext.Provider
              value={{ animation1, animation2, animation3, animation4 }}
            >
              <HeroSection content={heroSection} />
              {displayedSections.map((section, i: number) => {
                // @ts-ignore
                if (components[section.componentName]) {
                  return React.createElement(
                    // @ts-ignore
                    components[section.componentName],
                    {
                      content: section,
                      key: `${section.componentName}-${i}`, // handles the case for duplicate section keys
                    },
                  );
                }
              })}
            </RefsForAnimationContext.Provider>
          </AllProductPromosContext.Provider>
        </ProductInfoContext.Provider>
      </Layout>
    </>
  );
};

export default homepageTemplate;
